import { useEffect, useState } from "react";
import { mntAPI } from "./mntApi";
import { toast } from "sonner";
import { Plus, RefreshCw, CalendarClock } from "lucide-react";
import { Modal, F, cls, Footer } from "./MaintenanceWorkOrders";
import { NumberInput } from "@/components/ui/number-input";

const FREQ_LABELS = {
  daily: "Daily", weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly",
  half_yearly: "Every 6 months", yearly: "Yearly", custom_days: "Custom",
};

export default function MaintenancePM() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const load = () => mntAPI.pms().then(setRows).catch(() => {});
  useEffect(() => {
    load();
    mntAPI.assets({ limit: 500 }).then(setAssets);
    mntAPI.engineers().then(setEngineers);
  }, []);

  const sweep = async () => {
    setSweeping(true);
    try {
      const r = await mntAPI.pmSweep();
      toast.success(r.generated > 0
        ? `${r.generated} work order(s) generated`
        : "Nothing due — everything on schedule");
      load();
    } catch (e) { toast.error("Sweep failed"); }
    finally { setSweeping(false); }
  };

  return (
    <div className="space-y-4" data-testid="mnt-pm">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-[16px] font-semibold text-hocp-navy">Preventive Schedules</h2>
          <p className="text-[12.5px] text-slate-500">Recurring maintenance — auto-generates work orders when due.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={sweep} disabled={sweeping} data-testid="mnt-pm-sweep"
                  className="h-10 px-3.5 rounded-lg border border-slate-200 text-[13px] hover:bg-slate-50 inline-flex items-center gap-2 disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${sweeping ? "animate-spin" : ""}`}/> Run sweep now
          </button>
          <button onClick={() => setCreating(true)} disabled={!assets.length}
                  data-testid="mnt-pm-new"
                  className="h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50">
            <Plus className="w-4 h-4"/> New Schedule
          </button>
        </div>
      </div>

      {!rows.length ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-10 text-center text-[13px] text-slate-500">
          <CalendarClock className="w-6 h-6 mx-auto text-slate-300 mb-2"/>
          No preventive schedules yet. Add one to auto-generate recurring work orders.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3">Asset</th>
                <th className="text-left px-4 py-3">Frequency</th>
                <th className="text-left px-4 py-3">Next Due</th>
                <th className="text-left px-4 py-3">Priority</th>
                <th className="text-left px-4 py-3">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(pm => {
                const due = new Date(pm.next_due_at) <= new Date();
                return (
                  <tr key={pm.id} data-testid={`mnt-pm-row-${pm.id}`} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-hocp-navy">{pm.title}</td>
                    <td className="px-4 py-3 text-slate-600">{pm.asset_name}</td>
                    <td className="px-4 py-3 text-slate-600">{FREQ_LABELS[pm.frequency] || pm.frequency}{pm.custom_days ? ` (${pm.custom_days}d)` : ""}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[12px] mono ${due ? "text-rose-700 font-semibold" : "text-slate-600"}`}>
                        {(pm.next_due_at || "").slice(0, 10)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{pm.priority}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium ${pm.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {pm.is_active ? "Active" : "Paused"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && <PMDialog assets={assets} engineers={engineers}
                              onClose={() => setCreating(false)}
                              onDone={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

function PMDialog({ assets, engineers, onClose, onDone }) {
  const [f, setF] = useState({
    asset_id: assets[0]?.id || "", title: "",
    frequency: "monthly", custom_days: 30,
    priority: "medium", default_assignee_id: "",
    checklist: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.title || !f.asset_id) { toast.error("Title and asset are required"); return; }
    setSaving(true);
    try {
      await mntAPI.createPM({
        asset_id: f.asset_id, title: f.title, frequency: f.frequency,
        custom_days: f.frequency === "custom_days" ? Number(f.custom_days) : undefined,
        priority: f.priority,
        default_assignee_id: f.default_assignee_id || undefined,
        checklist: f.checklist ? f.checklist.split("\n").map(s => s.trim()).filter(Boolean) : [],
      });
      toast.success("Schedule created"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="New Preventive Schedule" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <F label="Title" full><input value={f.title} onChange={e => setF({...f, title: e.target.value})} data-testid="new-pm-title" placeholder="e.g. AC Quarterly Servicing" className={cls}/></F>
        <F label="Asset"><select value={f.asset_id} onChange={e => setF({...f, asset_id: e.target.value})} data-testid="new-pm-asset" className={cls}>
          {assets.map(a => <option key={a.id} value={a.id}>{a.asset_code} — {a.name}</option>)}
        </select></F>
        <F label="Priority"><select value={f.priority} onChange={e => setF({...f, priority: e.target.value})} className={cls}>
          <option value="emergency">Emergency</option><option value="high">High</option>
          <option value="medium">Medium</option><option value="low">Low</option>
        </select></F>
        <F label="Frequency"><select value={f.frequency} onChange={e => setF({...f, frequency: e.target.value})} data-testid="new-pm-freq" className={cls}>
          {Object.entries(FREQ_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select></F>
        {f.frequency === "custom_days" && (
          <F label="Every N days"><NumberInput value={f.custom_days} onChange={e => setF({...f, custom_days: e.target.value})} className={cls}/></F>
        )}
        <F label="Default engineer (optional)" full>
          <select value={f.default_assignee_id} onChange={e => setF({...f, default_assignee_id: e.target.value})} className={cls}>
            <option value="">— unassigned —</option>
            {engineers.map(en => <option key={en.id} value={en.id}>{en.name || en.email}</option>)}
          </select>
        </F>
        <F label="Checklist (one item per line)" full>
          <textarea rows={4} value={f.checklist} onChange={e => setF({...f, checklist: e.target.value})}
                    placeholder={"Check refrigerant\nClean filter\nInspect wiring"}
                    className={cls + " py-2 h-auto"}/>
        </F>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} testid="new-pm-submit"/>
    </Modal>
  );
}
